import { useReactiveVar } from '@apollo/client';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { ResponsiveMode } from '@fluentui/react/lib/ResponsiveMode';
import { Toggle } from '@fluentui/react/lib/Toggle';
import * as React from 'react';
import {
    ApproverScope,
    templateInstanceAllMustRespondVar,
    templateInstanceApproverScopeVar,
    templateInstanceApproversVar,
} from '../../../../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    currentTeamInCreationVar,
    TemplateScope,
} from '../../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { LogCategory } from '../../../../../../logger';
import { PeopleResult } from '../../../../../../service/gen/templates';
import { getAppHost } from '../../../../../../utilities/TeamsUtils';
import { PeopleResults2IPersonaProps, TemplateInstanceValidator } from '../../../../../../utilities/TemplateUtils';
import { stringFormat } from '../../../../../../utilities/Utils';
import { TemplatePeoplePicker } from '../../../../TemplatePeoplePicker';
import { DataSourceType } from '../../../../TemplatePeoplePicker/TemplatePeoplePicker.container';
import { getDropdownStyles } from '../WorkflowSettings.styles';

export interface TemplateInstanceApproverSettingsV1Props {
    approverScope: ApproverScope;
    onApproverScopeChange?: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => void;
    approvers?: PeopleResult[];
    onApproversChange?: (ApproversSelected: IPersonaProps[]) => void;
    approversError?: string;
    allMustRespond?: boolean;
    onAllMustRespondChange?: (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => void;
    dataSourceType: DataSourceType;
    dataSourceTeamId?: string;
}

/**
 * @return
 * 1. the props needed to render TemplateInstanceApproverSettingsV1 component
 * 2. validation result of current template instance approvers input
 *
 * @description
 * custom hook dedicated for TemplateInstanceApproverSettingsV1 component
 * return the props needed to render TemplateInstanceApproverSettingsV1 component to make the component easier to use
 * and also make the usage code and implementation code more simple and readable.
 *
 * @note
 * 1. This hook coupled with some Reactive Vars.
 * 2. The unit test of this hook go with the ut of TemplateInstanceApproverSettingsV1 componet
 */
export const useTemplateInstanceApproverSettingsV1 = (): TemplateInstanceApproverSettingsV1Props & {
    templateInstanceApproversValid: boolean;
} => {
    /* States of Appprover scope setting. */
    const approverScope = useReactiveVar(templateInstanceApproverScopeVar);
    const onApproverScopeChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        templateInstanceApproverScopeVar(option.key as ApproverScope);
    };

    /* States of Appprovers setting. */
    const approvers = useReactiveVar(templateInstanceApproversVar);
    const [approversError, setApproversError] = React.useState<string>(null);
    const onApproversChange = (approversSelected: IPersonaProps[]): void => {
        templateInstanceApproversVar(
            approversSelected.map((item: IPersonaProps): PeopleResult => {
                return { UserAadObjectId: item.id, UserDisplayName: item.text } as any;
            })
        );
        if (approversSelected?.length <= 1) {
            templateInstanceAllMustRespondVar(true);
        }
    };

    /* States of AllMustRespond setting. */
    const allMustRespond = useReactiveVar(templateInstanceAllMustRespondVar);
    const onAllMustRespondChange = (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean): void => {
        templateInstanceAllMustRespondVar(checked);
    };

    /* States of current Template Management state. */
    const currentScope = useReactiveVar(currentScopeInCreationVar);
    const currentTeam = useReactiveVar(currentTeamInCreationVar);
    const dataSourceType =
        currentScope === TemplateScope.Team ? DataSourceType.TeamWidePeople : DataSourceType.TenantWidePeople;

    /* This hook validate the Approvers field, when related fields update. */
    React.useEffect((): void => {
        const logger = getAppHost().getLogger();
        const loc = getAppHost().getLocale();

        /* In ApproverScope.LetSubmitterEnter scope, there is no input to validate, so always return true. */
        switch (approverScope) {
            case ApproverScope.LetSubmitterEnter:
                setApproversError(null);
                break;
            case ApproverScope.SpecifyApprovers:
                if (TemplateInstanceValidator.isApproversEmpty(approvers)) {
                    setApproversError(loc.strings.required);
                } else if (TemplateInstanceValidator.isApproversExceeding(approvers)) {
                    logger.logTraceWarning(LogCategory.TemplateInstanceWizard, 'Maximum number of approvers reached!');
                    setApproversError(loc.strings.maximum_approvers_error);
                } else {
                    setApproversError(null);
                }
                break;
        }
    }, [approvers, approverScope]);

    return {
        approverScope,
        onApproverScopeChange,
        approvers,
        onApproversChange,
        approversError,
        dataSourceType,
        dataSourceTeamId: currentTeam?.id,
        allMustRespond,
        onAllMustRespondChange,
        templateInstanceApproversValid: !approversError,
    };
};

export const TemplateInstanceApproverSettingsV1: React.FC<TemplateInstanceApproverSettingsV1Props> = (
    props: TemplateInstanceApproverSettingsV1Props
): JSX.Element => {
    const {
        approverScope,
        onApproverScopeChange,
        approvers,
        approversError,
        onApproversChange,
        allMustRespond,
        onAllMustRespondChange,
        dataSourceType,
        dataSourceTeamId,
    } = props;

    /* Approver Scope dropdown options. */
    const loc = getAppHost().getLocale();
    const approverScopeOptions = React.useMemo((): IDropdownOption[] => {
        return [
            { key: ApproverScope.LetSubmitterEnter, text: loc.strings.let_submitter_enter_option_text },
            { key: ApproverScope.SpecifyApprovers, text: loc.strings.specify_approvers_option_text },
        ];
    }, [loc]);

    return (
        <>
            <Dropdown
                label={loc.strings.approvers}
                styles={getDropdownStyles}
                responsiveMode={ResponsiveMode.large}
                options={approverScopeOptions}
                onChange={onApproverScopeChange}
                selectedKey={approverScope}
                ariaLabel={loc.strings.approver_option_aria_label}
            />
            {approverScope === ApproverScope.SpecifyApprovers && (
                <div className='template-management-specify-approver'>
                    <TemplatePeoplePicker
                        onApproversSelected={onApproversChange}
                        error={approversError}
                        currentSelectedItems={PeopleResults2IPersonaProps(approvers)}
                        label={loc.strings.visibility_scope_column_header}
                        dataSourceType={dataSourceType}
                        dataSourceTeamId={dataSourceTeamId}
                    />
                    <Toggle
                        disabled={!approvers || approvers.length < 2}
                        checked={allMustRespond}
                        label={loc.strings.all_must_respond_toggle_label}
                        inlineLabel
                        onChange={onAllMustRespondChange}
                        ariaLabel={stringFormat(loc.strings.all_must_approve_aria_label, {
                            state: allMustRespond ? loc.strings.checked_aria_label : loc.strings.unchecked_aria_label,
                        })}
                    />
                </div>
            )}
        </>
    );
};
